import type BulkManager from 'district-ui-client/utils/bulk-manager'
import type { Snapshot } from '@ember-data/legacy-compat/legacy-network-handler/snapshot'
import { assert } from '@ember/debug'
import type { AdapterPayload } from '@ember-data/legacy-compat'

type SerializedSnapshot = Partial<Record<string | number | symbol, unknown>> & {
  data?: Record<string, unknown>
}

export async function createRecordBulk(snapshot: Snapshot, bulkManager: BulkManager): Promise<AdapterPayload> {
  // the last line of the JSONApiSerializer.serialize() function will wrap the serialized model in a `data` key,
  // we are batching the create request hence the only `data` key in the payload should be the top level one e.g:
  //
  // {
  //   data: [serializedRecord, serializedRecord, ...]
  // }
  //
  // Use destructuring to unwrap each serialized model from the `data` key.
  //
  const serialized = snapshot.serialize({}) as SerializedSnapshot

  const result = await bulkManager.callBulkAction.perform('POST', serialized.data ? serialized.data : serialized)

  assert('createRecordBulk requires a result with data', result && typeof result === 'object')

  return result
}

export async function updateRecordBulk(_store: unknown, _type: unknown, snapshot: Snapshot, bulkManager: BulkManager) {
  let data = (snapshot.serialize() as SerializedSnapshot).data
  assert('updateRecordBulk requires a snapshot with data', data && typeof data === 'object')

  data = data as Record<string, unknown> & { id: Nullable<string> }

  data.id = snapshot.id

  return (await bulkManager.callBulkAction.perform('PATCH', data)) as AdapterPayload
}

export async function deleteRecordBulk(_store: unknown, _type: unknown, snapshot: Snapshot, bulkManager: BulkManager) {
  // Build an object with id and model name
  const data = {
    id: snapshot.id,
    type: snapshot.modelName,
  }

  const additionalBulkData = snapshot.adapterOptions?.additionalBulkData

  if (additionalBulkData && typeof additionalBulkData === 'object') {
    return (await bulkManager.callBulkAction.perform(
      'DELETE',
      data,
      additionalBulkData as Record<string, unknown>,
    )) as AdapterPayload
  } else {
    return (await bulkManager.callBulkAction.perform('DELETE', data)) as AdapterPayload
  }
}
